import { SideNav } from "@design-system/SideNav/SideNav";
import { SideNavItem } from "@design-system/SideNav/SideNavItem";
import { SideNavSection } from "@design-system/SideNav/SideNavSection";
import {
    CalendarIcon,
    ChartOfAccountsIcon,
    ClockIcon,
    HomeGridIcon,
    HomeIcon,
    RulesIcon,
    ServicesIcon,
    SkillsIcon,
    TransactionsIcon,
} from "@icons";
import {
    PlaygroundAddClientButton,
    PlaygroundEntityIcon,
    PlaygroundHeader,
    PlaygroundHelpButton,
    PlaygroundTaskSubItems,
    PlaygroundTopRows,
    usePlaygroundNav,
    type SideNavCompositionProps,
} from "./SideNavPlaygroundParts";

const RECENT_CLIENTS = [
    "Northwind Holdings",
    "Zhong & Sanches",
    "Bluebird Bakery",
    "Harbor Dental Group",
    "Copperline Studio",
];

/**
 * Org nav per the Migration map, plus what ships hidden: the Automation
 * section and client task sub-rows in all three statuses.
 */
export const OrgSideNav = ({
    className,
    forcedRowState,
}: SideNavCompositionProps) => {
    const nav = usePlaygroundNav({
        activeId: "home",
        expandedRows: ["client:Northwind Holdings"],
    });

    return (
        <SideNav
            aria-label="Main"
            className={className}
            header={<PlaygroundHeader name="Ledgerline CPAs" shape="square" />}
            top={
                <>
                    <PlaygroundTopRows
                        nav={nav}
                        taskCount={7}
                        home={
                            <SideNavItem
                                {...nav.link("home")}
                                icon={<HomeIcon />}
                                label="Home"
                            />
                        }
                    />
                    <SideNavItem
                        {...nav.link("activity")}
                        icon={<ClockIcon />}
                        label="Activity"
                    />
                </>
            }
            footer={<PlaygroundHelpButton />}
        >
            <SideNavSection
                {...nav.section("Templates")}
                data-forced-state={forcedRowState}
            >
                <SideNavItem
                    {...nav.link("coa-templates")}
                    icon={<ChartOfAccountsIcon />}
                    label="Chart of Accounts"
                    data-forced-state={forcedRowState}
                />
                <SideNavItem
                    {...nav.link("services")}
                    icon={<ServicesIcon />}
                    label="Services"
                />
                <SideNavItem
                    {...nav.link("schedules")}
                    icon={<CalendarIcon />}
                    label="Schedules"
                />
            </SideNavSection>
            <SideNavSection
                {...nav.section("Clients")}
                action={<PlaygroundAddClientButton />}
            >
                {RECENT_CLIENTS.map((client) => (
                    <SideNavItem
                        key={client}
                        {...nav.link(`client:${client}`)}
                        {...nav.expansion(`client:${client}`)}
                        icon={<PlaygroundEntityIcon name={client} />}
                        label={client}
                    >
                        <PlaygroundTaskSubItems
                            target={(taskId) => nav.link(`${client}:${taskId}`)}
                        />
                    </SideNavItem>
                ))}
                <SideNavItem
                    {...nav.link("all-clients")}
                    icon={<TransactionsIcon />}
                    label="All clients"
                />
            </SideNavSection>
            <SideNavSection {...nav.section("Automation")}>
                <SideNavItem
                    {...nav.link("org-rules")}
                    icon={<RulesIcon />}
                    label="Rules"
                />
                <SideNavItem
                    {...nav.link("org-skills")}
                    icon={<SkillsIcon />}
                    label="Skills"
                />
            </SideNavSection>
        </SideNav>
    );
};

/** Personal nav: the picker header plus a Workspaces row. */
export const PersonalSideNav = ({
    className,
    forcedRowState,
}: SideNavCompositionProps) => {
    const nav = usePlaygroundNav({ activeId: "workspaces" });

    return (
        <SideNav
            aria-label="Main"
            className={className}
            header={<PlaygroundHeader name="Dana Whitfield" shape="circle" />}
            top={
                <SideNavItem
                    {...nav.link("workspaces")}
                    icon={<HomeGridIcon />}
                    label="Workspaces"
                    data-forced-state={forcedRowState}
                />
            }
            footer={<PlaygroundHelpButton />}
        />
    );
};
